// Kétlépcsős hitelesítés (TOTP) kártya — a teljes 2FA-életciklus a self-service /profile/2fa/* endpointokon:
//   • KIKAPCSOLT → „Beállítás" (setup) → QR-kód az otpauthUri-ból (kliens-oldali SVG, qrcode.react) + manuális
//     titok → kód-input → „Aktiválás" (enable) → 10 recovery-kód EGYSZERI megjelenítése (dialógus).
//   • BEKAPCSOLT → „Kikapcsolás" (step-up: kód VAGY jelszó) + „Recovery-kódok újragenerálása" (step-up).
// BIZTONSÁG: a secret és a recovery-kódok CSAK memóriában élnek (state) — sosem perzisztáljuk / logoljuk; a
// dialógus bezárásakor töröljük őket.
import {
  ArrowPathIcon,
  ClipboardDocumentIcon,
  ArrowDownTrayIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { DomainBadge } from '@/components/shared/domain-badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ImpersonationFence } from '@/features/impersonation/components/impersonation-fence';
import { profileKeys } from '@/features/profile/api';
import { ProfileSection } from '@/features/profile/components/profile-section';
import { EMPTY_STEP_UP, hasStepUp, stepUpBody, type StepUpValues } from '@/features/profile/form';
import { api } from '@/lib/api';
import { copyWithToast } from '@/lib/clipboard';

/** Recovery-kódok EGYSZERI megjelenítése — másolás / letöltés + kötelező „Elmentettem" megerősítés a bezáráshoz. */
function RecoveryCodesDialog({
  codes,
  onClose,
}: {
  codes: string[];
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const text = codes.join('\n');

  const copy = (): void => {
    void copyWithToast(text, {
      success: t('toast:profile.copied'),
      error: t('toast:profile.copyFailed'),
    });
  };
  const download = (): void => {
    const blob = new Blob([text + '\n'], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'atlas-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        {/* items-start: a hosszú (több soros) leírásnál is a CÍM sorába kerüljön a záró X (nem a sorok közé). */}
        <DialogHeader className="items-start">
          <DialogTitle>{t('profile.twoFactor.recovery.title')}</DialogTitle>
          <DialogDescription>{t('profile.twoFactor.recovery.desc')}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          {/* A kódok a blokk bal oldalán; jobbra, függőlegesen középen, EGYMÁS ALATT a másolás/letöltés ikon-gombok. */}
          <div className="bg-muted/50 flex items-center gap-4 rounded-lg p-4">
            <ul className="grid flex-1 grid-cols-2 gap-x-6 gap-y-1.5 font-mono text-sm">
              {codes.map((code) => (
                <li key={code} className="tracking-wider">
                  {code}
                </li>
              ))}
            </ul>
            <div className="flex shrink-0 flex-col gap-1.5">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={copy}
                    aria-label={t('profile.twoFactor.recovery.copy')}
                  >
                    <ClipboardDocumentIcon />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('profile.twoFactor.recovery.copy')}</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={download}
                    aria-label={t('profile.twoFactor.recovery.download')}
                  >
                    <ArrowDownTrayIcon />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('profile.twoFactor.recovery.download')}</TooltipContent>
              </Tooltip>
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="brand" onClick={onClose}>
            {t('profile.twoFactor.recovery.saved')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Step-up dialógus (kikapcsolás / recovery-újragenerálás): TOTP/recovery KÓD VAGY jelenlegi jelszó. */
function StepUpDialog({
  title,
  desc,
  confirmLabel,
  pending,
  onConfirm,
  onClose,
}: {
  title: string;
  desc: string;
  confirmLabel: string;
  pending: boolean;
  onConfirm: (v: StepUpValues) => void;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [values, setValues] = useState<StepUpValues>(EMPTY_STEP_UP);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        {/* items-start: a több soros leírásnál is a CÍM sorába kerüljön a záró X. */}
        <DialogHeader className="items-start">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{desc}</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="stepup-code">{t('profile.twoFactor.stepUp.code')}</Label>
            <Input
              id="stepup-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={values.code}
              onChange={(e) => setValues((s) => ({ ...s, code: e.target.value }))}
            />
          </div>
          <div className="text-subtle text-center text-xs">{t('profile.twoFactor.stepUp.or')}</div>
          <div className="space-y-1.5">
            <Label htmlFor="stepup-pw">{t('profile.twoFactor.stepUp.password')}</Label>
            <PasswordInput
              id="stepup-pw"
              autoComplete="current-password"
              value={values.currentPassword}
              onChange={(e) => setValues((s) => ({ ...s, currentPassword: e.target.value }))}
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={pending}>
            {t('profile.twoFactor.stepUp.cancel')}
          </Button>
          <Button
            variant="brand"
            type="button"
            disabled={pending || !hasStepUp(values)}
            onClick={() => onConfirm(values)}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TwoFactorCard({
  mfaEnabled,
  twoFactorPending,
}: {
  mfaEnabled: boolean;
  twoFactorPending: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // Setup fázis (kliens-memória): a titok + otpauth-URI CSAK a beállítás alatt él.
  const [setup, setSetup] = useState<{ secret: string; otpauthUri: string } | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [disableOpen, setDisableOpen] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: profileKeys.detail() });
  };

  const setupM = api.profileTwoFactorSetup.useMutation();
  const enableM = api.profileTwoFactorEnable.useMutation();
  const disableM = api.profileTwoFactorDisable.useMutation();
  const regenM = api.profileTwoFactorRecoveryCodes.useMutation();

  const startSetup = (): void => {
    setupM.mutate(
      { body: {} },
      {
        onSuccess: (res) => {
          if (res.status === 200) {
            setSetup(res.body);
            setCode('');
          } else if (res.status === 409) {
            toast.error(t('toast:profile.twoFactorAlreadyOn'));
          } else {
            toast.error(t('toast:profile.saveError'));
          }
        },
        onError: () => toast.error(t('toast:profile.saveError')),
      },
    );
  };

  const confirmEnable = (): void => {
    enableM.mutate(
      { body: { code: code.trim() } },
      {
        onSuccess: (res) => {
          if (res.status === 200) {
            setRecoveryCodes(res.body.recoveryCodes);
            setSetup(null);
            setCode('');
            invalidate();
            toast.success(t('toast:profile.twoFactorEnabled'));
          } else if (res.status === 422) {
            toast.error(t('toast:profile.twoFactorBadCode'));
          } else {
            toast.error(t('toast:profile.saveError'));
          }
        },
        onError: () => toast.error(t('toast:profile.saveError')),
      },
    );
  };

  const confirmDisable = (v: StepUpValues): void => {
    disableM.mutate(
      { body: stepUpBody(v) },
      {
        onSuccess: (res) => {
          if (res.status === 200) {
            setDisableOpen(false);
            invalidate();
            toast.success(t('toast:profile.twoFactorDisabled'));
          } else if (res.status === 403) {
            toast.error(t('toast:profile.stepUpFailed'));
          } else {
            toast.error(t('toast:profile.saveError'));
          }
        },
        onError: () => toast.error(t('toast:profile.saveError')),
      },
    );
  };

  const confirmRegen = (v: StepUpValues): void => {
    regenM.mutate(
      { body: stepUpBody(v) },
      {
        onSuccess: (res) => {
          if (res.status === 200) {
            setRegenOpen(false);
            setRecoveryCodes(res.body.recoveryCodes);
            invalidate();
          } else if (res.status === 403) {
            toast.error(t('toast:profile.stepUpFailed'));
          } else {
            toast.error(t('toast:profile.saveError'));
          }
        },
        onError: () => toast.error(t('toast:profile.saveError')),
      },
    );
  };

  return (
    <ProfileSection
      title={t('profile.twoFactor.title')}
      description={t('profile.twoFactor.desc')}
      action={
        mfaEnabled ? (
          <DomainBadge kind="twoFactorStatus" value="on" label={t('profile.twoFactor.statusOn')} />
        ) : twoFactorPending ? (
          <DomainBadge
            kind="twoFactorStatus"
            value="pending"
            label={t('profile.twoFactor.statusPending')}
          />
        ) : (
          <DomainBadge
            kind="twoFactorStatus"
            value="off"
            label={t('profile.twoFactor.statusOff')}
          />
        )
      }
    >
      {/* MEGSZEMÉLYESÍTÉS-FAL: a `/iam/me/2fa/*` végpontok mind `@DenyImpersonation()`-ök (403
          `impersonation_fenced`) — a beállítás/kikapcsolás/recovery-kód más nevében nem megy. */}
      {/* `hint={false}`: a magyarázat a lap tetején, az „Alapadatok" fölött áll (egy lapon egyszer). */}
      <ImpersonationFence hint={false}>
        <div className="space-y-4">
          {mfaEnabled ? (
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="destructive" onClick={() => setDisableOpen(true)}>
                <ShieldExclamationIcon />
                {t('profile.twoFactor.disable')}
              </Button>
              <Button type="button" variant="outline" onClick={() => setRegenOpen(true)}>
                <ArrowPathIcon />
                {t('profile.twoFactor.regenerate')}
              </Button>
            </div>
          ) : setup ? (
            <div className="space-y-4">
              <p className="text-sm">{t('profile.twoFactor.scanHint')}</p>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                {/* A QR mindig fehér alapon renderel (sötét témában is szkennelhető). */}
                <div className="w-fit rounded-lg bg-white p-3">
                  <QRCodeSVG value={setup.otpauthUri} size={160} level="M" />
                </div>
                {/* Jobb oszlop a QR mellett: kézi beállítókulcs, ALATTA a hitelesítő kód mező (elfér). */}
                <div className="min-w-0 flex-1 space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="manual-key">{t('profile.twoFactor.manualKey')}</Label>
                    {/* Kézi beállítókulcs: readonly input + jobbra, a mezőn BELÜL vágólapra-másolás ikon. */}
                    <div className="relative">
                      <Input
                        id="manual-key"
                        readOnly
                        value={setup.secret}
                        className="pr-9 font-mono"
                        onFocus={(e) => e.currentTarget.select()}
                      />
                      <button
                        type="button"
                        onClick={() =>
                          void copyWithToast(setup.secret, {
                            success: t('toast:profile.copied'),
                            error: t('toast:profile.copyFailed'),
                          })
                        }
                        aria-label={t('profile.twoFactor.copyKey')}
                        title={t('profile.twoFactor.copyKey')}
                        className="text-muted-foreground hover:text-foreground focus-visible:text-foreground absolute inset-y-0 right-0 flex w-9 items-center justify-center transition-colors focus-visible:outline-none"
                      >
                        <ClipboardDocumentIcon className="size-4" />
                      </button>
                    </div>
                    <p className="text-subtle text-xs">{t('profile.twoFactor.manualHint')}</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="enable-code">{t('profile.twoFactor.codeLabel')}</Label>
                    <Input
                      id="enable-code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={confirmEnable}
                  disabled={enableM.isPending || code.trim() === ''}
                >
                  <ShieldCheckIcon />
                  {t('profile.twoFactor.activate')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setSetup(null);
                    setCode('');
                  }}
                  disabled={enableM.isPending}
                >
                  {t('profile.twoFactor.stepUp.cancel')}
                </Button>
              </div>
            </div>
          ) : (
            // BALRA — ez a gomb a szekció TARTALMÁBAN áll, nem egy sávban: folyamatot INDÍT (mint a „Jelszó
            // módosítása"). Jobbra csak az zár, ami sáv/lábléc vagy határolt doboz akciója (design.md).
            <div className="flex">
              <Button
                type="button"
                variant="outline"
                onClick={startSetup}
                disabled={setupM.isPending}
              >
                <ShieldCheckIcon />
                {t('profile.twoFactor.setup')}
              </Button>
            </div>
          )}
        </div>

        {recoveryCodes && (
          <RecoveryCodesDialog codes={recoveryCodes} onClose={() => setRecoveryCodes(null)} />
        )}
        {disableOpen && (
          <StepUpDialog
            title={t('profile.twoFactor.disableDialog.title')}
            desc={t('profile.twoFactor.disableDialog.desc')}
            confirmLabel={t('profile.twoFactor.disable')}
            pending={disableM.isPending}
            onConfirm={confirmDisable}
            onClose={() => setDisableOpen(false)}
          />
        )}
        {regenOpen && (
          <StepUpDialog
            title={t('profile.twoFactor.regenDialog.title')}
            desc={t('profile.twoFactor.regenDialog.desc')}
            confirmLabel={t('profile.twoFactor.regenerate')}
            pending={regenM.isPending}
            onConfirm={confirmRegen}
            onClose={() => setRegenOpen(false)}
          />
        )}
      </ImpersonationFence>
    </ProfileSection>
  );
}
